/* eslint-disable no-console */
import { getTracerModules } from '@apptracer/sdk/lite';
import { jwtDecode } from 'jwt-decode';

import type { TokenManagerData } from '../../types/token-manager';

import { packageName, widgetEnableVkIdAutologin } from '../../../config';
import { OLD_TOKEN_KEY, TOKEN_KEY, HAS_MAX_USER_PHONE } from '../../constants/localStorage';
import { getAutologinRedirect } from '../getAutologinRedirect';
import { storageManager } from '../storageManager';

type ObserverFunction = (data?: TokenManagerData) => void;

class TokenManager {
  private token: string | null;
  private oldToken: string | null;
  private observers: ObserverFunction[];
  private tracerError = getTracerModules(packageName).error;

  constructor() {
    this.observers = [];
    // Инициализируем токены из стораджа с учётом того, что он может быть недоступен
    try {
      this.token = storageManager?.getItem(TOKEN_KEY) || null;
      this.oldToken = storageManager?.getItem(OLD_TOKEN_KEY) || null;
    } catch (error) {
      this.tracerError?.error('Ошибка получения токенов из хранилища');
      console.error('Error initializing tokens from storage:', error);
      this.token = null;
      this.oldToken = null;
    }
  }

  subscribe(func: ObserverFunction) {
    this.observers.push(func);
  }

  unsubscribe(func: ObserverFunction) {
    this.observers = this.observers.filter((observer) => observer !== func);
  }

  notify(data?: TokenManagerData) {
    this.observers.forEach((observer) => observer(data));
  }

  getToken() {
    return this.token;
  }

  getOldToken() {
    return this.oldToken;
  }

  setOldToken(token: string) {
    this.oldToken = token;
    try {
      storageManager?.setItem(OLD_TOKEN_KEY, token);
    } catch (_) {
      this.tracerError?.error('Ошибка получения старого токена из хранилища');
    }
  }

  getIsTokenExpired() {
    if (this.token) {
      const { exp } = jwtDecode(this.token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (exp) {
        return exp < currentTime;
      }
    }

    return true;
  }

  /**
   * Извлекает userUuid из JWT payload (поле sub).
   * Единственный источник userUuid — текущий токен.
   */
  getUserUuid(): string | null {
    if (!this.token) return null;

    try {
      const decoded = jwtDecode<Record<string, unknown>>(this.token);
      return (decoded.sub as string) || null;
    } catch {
      return null;
    }
  }

  setAuthData(data: TokenManagerData) {
    const { token, userUuid } = data;

    if (token && userUuid) {
      this.token = token;

      try {
        storageManager?.setItem(TOKEN_KEY, token);
      } catch (_) {
        this.tracerError?.error('Ощибка сохранения данных авторизации в хранилище', {
          keys: {
            userUuid,
          },
        });
      }

      this.notify(data);
    }
  }

  removeAuthData() {
    try {
      storageManager?.removeItem(TOKEN_KEY);
      storageManager?.removeItem(HAS_MAX_USER_PHONE);
    } catch (_) {
      this.tracerError?.error('Ошибка удаления данных авторизации из хранилища', {
        keys: {
          hasMaxUserPhone: HAS_MAX_USER_PHONE,
        },
      });
    }

    this.token = null;

    this.notify();
  }

  /** Очистка ключей/токенов при 500 от /validate */
  clearAllAuthAndKeys() {
    try {
      storageManager?.removeItem(TOKEN_KEY);
      storageManager?.removeItem(OLD_TOKEN_KEY);
      storageManager?.removeItem(HAS_MAX_USER_PHONE);
    } catch (_) {
      this.tracerError?.error('Ошибка удаления данных авторизации и ключей из хранилища', {
        keys: {
          hasMaxUserPhone: HAS_MAX_USER_PHONE,
        },
      });
    }

    this.token = null;
    this.oldToken = null;

    this.notify();
  }

  async refreshToken(widgetUuid?: string) {
    // Если у токена истёк срок жизни
    const token = this.getToken();
    const isTokenExpired = this.getIsTokenExpired();

    if (token && isTokenExpired) {
      this.setOldToken(token);

      this.removeAuthData();

      if (widgetUuid && widgetEnableVkIdAutologin === 'true') {
        // Идем за новым silent токеном, меняем его на jwt и проставляем в localStorage
        getAutologinRedirect(widgetUuid);
      }
    }
  }
}

export const tokenManager = new TokenManager();
